/**
 * Formats a plan's `price_inr` (paise, GST-EXCLUSIVE catalog price since
 * 2026-10-17 — checkout charges price + 18% GST) into a rupee display
 * string with Indian digit grouping (lakh/crore), e.g. 99900 paise → "₹999".
 *
 * `period` appends the house-style "/mo" or "/yr" suffix used on the 2×2
 * pricing grid.
 */
export function formatPriceInr(
  priceInPaise: number,
  period: 'monthly' | 'annual',
): string {
  const rupees = Math.round(priceInPaise / 100);
  const suffix = period === 'monthly' ? '/mo' : '/yr';
  return `₹${rupees.toLocaleString('en-IN')}${suffix}`;
}

/** Exact paise as rupees with two decimals and Indian grouping:
 *  176882 -> "₹1,768.82". For amounts that are charged, never rounded. */
export function formatPaiseExact(paise: number): string {
  return `₹${(paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** The line shown before payment: "₹1,768.82 (₹1,499 + ₹269.82 GST)".
 *  Uses the engine's own figures; when an older engine sends no total, says
 *  "+ 18% GST" rather than computing tax in the browser. */
export function chargeSummary(plan: {
  price_inr: number;
  gst_inr?: number;
  total_inr?: number;
}): string {
  if (plan.total_inr === undefined || plan.gst_inr === undefined) {
    return `${formatPaiseExact(plan.price_inr)} + 18% GST`;
  }
  return `${formatPaiseExact(plan.total_inr)} (${formatPaiseExact(plan.price_inr)} + ${formatPaiseExact(plan.gst_inr)} GST)`;
}
